"use client";

import { startNewRelicMemoryTracking } from "@/utils/monitoring/newrelicMemory";
import { useEffect } from "react";

interface NewRelicMemoryTrackerProps {
  /** Interval in milliseconds between memory snapshots. Default: 60000 (1 minute) */
  intervalMs?: number;
}

/**
 * Component that starts memory & performance tracking on mount.
 * - Sends metrics to New Relic in production
 * - Logs to console in local dev (when New Relic is not available)
 *
 * Add this component high in your component tree (e.g., in ClientProviders).
 *
 * Tracks:
 * - JS Heap size (Chrome only)
 * - DOM node count
 * - Long tasks (> 50ms)
 * - Navigation timing (TTFB, domComplete, loadTime)
 * - Session duration
 *
 * Data will be available in New Relic via NRQL:
 * ```sql
 * -- Memory by page
 * SELECT average(usedHeapMB), max(usedHeapMB), average(domNodes)
 * FROM PageAction WHERE actionName = 'MemorySnapshot'
 * FACET pathname SINCE 1 day ago
 *
 * -- Long task impact
 * SELECT sum(longTaskTotalDurationMs), sum(longTaskCount)
 * FROM PageAction WHERE actionName = 'MemorySnapshot'
 * FACET pathname SINCE 1 day ago
 *
 * -- Memory over time
 * SELECT average(usedHeapMB) FROM PageAction
 * WHERE actionName = 'MemorySnapshot'
 * TIMESERIES 1 hour SINCE 1 day ago
 * ```
 */
export function NewRelicMemoryTracker({
  intervalMs = 60000,
}: NewRelicMemoryTrackerProps) {
  useEffect(() => {
    const stopTracking = startNewRelicMemoryTracking(intervalMs);

    return () => {
      stopTracking();
    };
  }, [intervalMs]);

  return null;
}
